import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../lib/supabaseClient';
import { updatePreferencesLocal } from '../store/authSlice';

export const usePreferences = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  
  // Default to empty object if undefined and normalize keys for compatibility
  const rawPreferences = user?.preferences || {};
  // Support both snake_case and camelCase for all preference keys
  const preferences = {
    ...rawPreferences,
    date_format: rawPreferences.date_format || rawPreferences.dateFormat,
    dateFormat: rawPreferences.dateFormat || rawPreferences.date_format,
    default_landing_page: rawPreferences.default_landing_page || rawPreferences.defaultLandingPage,
    defaultLandingPage: rawPreferences.defaultLandingPage || rawPreferences.default_landing_page,
  };

  const updatePreference = useCallback(async (key, value) => {
    if (!user?.id) {
      console.warn('Cannot update preference: No authenticated user.');
      return;
    }

    console.log(`📝 Updating preference: ${key} = ${value}`);
    
    // We create the new partial object for local and the fully merged object for DB
    // Accept either camelCase or snake_case keys — persist using the same key provided
    const newPartial = { [key]: value };
    const mergedPreferences = { ...rawPreferences, ...newPartial };
    
    try {
      // Optimistically update the Redux store instantly
      dispatch(updatePreferencesLocal(newPartial));

      // Push merged object to Supabase
      const { error } = await supabase
        .from('profiles')
        .update({ preferences: mergedPreferences })
        .eq('id', user.id);

      if (error) {
        console.error('❌ Error updating preference in Supabase:', error);
        // In a more complex scenario, we could dispatch an action to revert the state here
        throw error;
      }
      console.log(`✅ Preference saved: ${key}`);
    } catch (err) {
      console.error('Failed to update preference:', err);
    }
  }, [user?.id, preferences, dispatch]);

  return {
    preferences,
    updatePreference,
  };
};
